import React, { useState, useEffect } from 'react';
import { adminApi } from '../../repositories/admin'; // Adjust path as necessary
import Swal from 'sweetalert2';
import { X, ChevronDown } from 'lucide-react';
import MultiSelect from '../../PitchFormPage/components/MultiSelect/MultiSelect';
import Button from '../ui/Button';

interface EditLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  labelId: number;
  initialName: string;
  initialType: string;
  initialAvailableDSPs: string[];
  initialAvailableAnalyticsDSPs: string[];
  onSave: (name: string, type: string, availableDSPs: string[], availableAnalyticsDSPs: string[]) => void;
}

const EditLabelModal: React.FC<EditLabelModalProps> = ({
  isOpen,
  onClose,
  labelId,
  initialName,
  initialType,
  initialAvailableDSPs,
  initialAvailableAnalyticsDSPs,
  onSave,
}) => {
  const [name, setName] = useState(initialName);
  const [type, setType] = useState(initialType);
  const [dsps, setDsps] = useState<string[]>(initialAvailableDSPs);
  const [analyticsDsps, setAnalyticsDsps] = useState<string[]>(initialAvailableAnalyticsDSPs);
  const musicPlatforms = [
    "Select All",
    'Spotify',
    'Apple Music',
    'Deezer for Creators',
    'SoundCloud',
    'TIDAL',
    'Amazon Music',
    'Audiomack',
    'YouTube',
    'Vevo',
    'Email',
    'SiriusXM',
  ];

  useEffect(() => {
    setName(initialName);
    setType(initialType);
    setDsps(initialAvailableDSPs);
    setAnalyticsDsps(initialAvailableAnalyticsDSPs);
  }, [initialName, initialType, initialAvailableDSPs, initialAvailableAnalyticsDSPs]);

  const handleClose = () => {
    setName(initialName);
    setType(initialType);
    setDsps(initialAvailableDSPs);
    setAnalyticsDsps(initialAvailableAnalyticsDSPs);
    onClose();
  };
  const handleSave = async () => {
    try {
      if (!name || !type || dsps.length === 0 || analyticsDsps.length === 0) {
        return Swal.fire('Error', 'Please fill in all fields', 'error');
      }
      onSave(
        name,
        type,
        dsps.filter((dsp) => dsp !== 'Select All'),
        analyticsDsps.filter((dsp) => dsp !== 'Select All')
      );
      onClose();
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Error updating label', 'error');
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-md p-6 bg-white shadow-lg rounded-2xl">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
          type="button"
          aria-label="Close"
        >
          <X />
        </button>
        {/*
          "Company" throughout, matching the page ("Client Management" /
          "Add Company") — this modal was the only surface still calling the
          same record a Label.
        */}
        <h2 className="mb-6 text-center text-lg font-semibold text-black">Edit Company</h2>
        <div>
          <label className="block mb-2 text-sm font-medium text-neutral-700">Company Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-3 py-2 text-sm text-black transition-colors bg-white border rounded-md outline-none border-neutral-300 placeholder-neutral-400 focus:border-primary-500"
          />
        </div>
        <div className="mt-4">
          <label className="block mb-2 text-sm font-medium text-neutral-700">Company Type</label>
          <div className="relative">
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              required
              className="w-full px-3 py-2 text-sm text-black transition-colors bg-white border rounded-md outline-none appearance-none cursor-pointer pr-10 border-neutral-300 focus:border-primary-500"
            >
              <option value="">Select Type</option>
              <option value="Indie Label">Indie Label</option>
              <option value="Major Label">Major Label</option>
              <option value="Management Company">Management Company</option>
              <option value="Distributor">Distributor</option>
              <option value="Music Services">Music Services</option>
              <option value="Publisher">Publisher</option>
              <option value="Major Affiliated Indie">Major Affiliated Indie</option>
            </select>
            <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-neutral-400" />
          </div>
        </div>
        <div className="mt-4">
          <label className="block mb-2 text-sm font-medium text-neutral-700">Available DSPs for this company</label>
          <MultiSelect value={dsps} onChange={setDsps} options={musicPlatforms} placeholder="Select DSPs" />
        </div>
        <div className="mt-4">
          <label className="block mb-2 text-sm font-medium text-neutral-700">Available DSP analytics</label>
          <MultiSelect value={analyticsDsps} onChange={setAnalyticsDsps} options={musicPlatforms} placeholder="Select DSPs" />
        </div>

        <div className="mt-6">
          <Button
            onClick={handleSave}
            className="w-full"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditLabelModal;